"use client";

import React from "react";
import { Button } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { ModalOverlay, ModalHeader, ModalBody, ModalFooter } from "@/components/UI/ModalOverlay";

const colorToVariant: Record<string, "primary" | "danger" | "secondary"> = {
  primary: "primary",
  success: "primary",
  warning: "secondary",
  danger: "danger",
};

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: "primary" | "success" | "warning" | "danger";
  icon?: string;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  confirmColor = "primary",
  icon = "solar:question-circle-bold",
}) => {
  const { t } = useTranslation();

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay isOpen={isOpen} size="md" onClose={onClose}>
      <ModalHeader className="flex items-center gap-3">
        <Icon className="text-default-500" icon={icon} width={24} />
        <span>{title}</span>
      </ModalHeader>
      <ModalBody>
        <p className="text-default-600 whitespace-pre-line leading-relaxed">{message}</p>
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onPress={onClose}>
          {cancelText || t("Cancel")}
        </Button>
        <Button variant={colorToVariant[confirmColor] || "primary"} onPress={handleConfirm}>
          {confirmText || t("Continue")}
        </Button>
      </ModalFooter>
    </ModalOverlay>
  );
};
